import { logger } from '@vagabondinternet/logging';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DailySchedule, DailyScheduleSource } from './daily-schedule-source';

export interface DailyScheduleState {
  /** Undefined until the first schedule arrives. */
  schedule?: DailySchedule;
  /** Fetches the schedule again, e.g. when the current daily ends. */
  refetch: () => void;
}

/** Loads the daily schedule from `source`, keeping the last good schedule if a refetch fails. */
export const useDailySchedule = (source: DailyScheduleSource): DailyScheduleState => {
  const [schedule, setSchedule] = useState<DailySchedule>();
  const request = useRef(0);

  const refetch = useCallback(() => {
    // Only the latest request may land, so a slow response can't overwrite a newer one.
    const id = ++request.current;
    source
      .getSchedule()
      .then((next) => {
        if (id === request.current) setSchedule(next);
      })
      .catch((error: unknown) => logger.error({ message: 'Failed to load the daily schedule', error }));
  }, [source]);

  useEffect(() => {
    refetch();
    return () => {
      request.current += 1;
    };
  }, [refetch]);

  return { schedule, refetch };
};
